import type { CourtCreate } from "@/libs/facilityApi";

export const MAX_BULK_COURTS = 60;

export interface CourtDraftIssue {
  rowNumber: number;
  field: "name" | "max_players" | "tags" | "csv";
  code:
    | "invalid_name"
    | "invalid_capacity"
    | "invalid_tags"
    | "duplicate_in_draft"
    | "invalid_csv"
    | "too_few_rows"
    | "too_many_rows";
  message: string;
}

export interface CourtRangeInput {
  prefix: string;
  startNumber: number;
  count: number;
  zeroPadding: number;
  maxPlayers?: number;
  tags: string[];
}

export interface ParsedCourtCsv {
  courts: CourtCreate[];
  issues: CourtDraftIssue[];
}

const COURT_NAME_EDGE_WHITESPACE = new Set(
  "\t\n\v\f\r" +
    "\x1c\x1d\x1e\x1f" +
    " " +
    "\x85\xa0\u1680" +
    "\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a" +
    "\u2028\u2029\u202f\u205f\u3000",
);

function trimCourtName(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && COURT_NAME_EDGE_WHITESPACE.has(value[start])) {
    start += 1;
  }
  while (end > start && COURT_NAME_EDGE_WHITESPACE.has(value[end - 1])) {
    end -= 1;
  }
  return value.slice(start, end);
}

function draftDuplicateKey(value: string): string {
  // PostgreSQL owns locale-sensitive case comparison during the mandatory
  // dry run. The client only blocks exact duplicates after the shared trim.
  return trimCourtName(value);
}

export function normalizeCourtTags(tags: string[]): string[] {
  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const rawTag of tags) {
    const tag = rawTag.trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(tag);
  }
  return normalized;
}

function validateCapacity(maxPlayers: number | undefined): void {
  if (
    maxPlayers !== undefined &&
    (!Number.isInteger(maxPlayers) || maxPlayers < 1 || maxPlayers > 50)
  ) {
    throw new Error("Court capacity must be a whole number from 1 to 50.");
  }
}

function validateTags(tags: string[]): void {
  if (tags.length > 20 || tags.some((tag) => Array.from(tag).length > 50)) {
    throw new Error("Use at most 20 tags, each no longer than 50 characters.");
  }
}

export function generateCourtRange(input: CourtRangeInput): CourtCreate[] {
  if (
    !Number.isSafeInteger(input.count) ||
    input.count < 1 ||
    input.count > 60
  ) {
    throw new Error("Court count must be a whole number from 1 to 60.");
  }
  if (!Number.isSafeInteger(input.startNumber) || input.startNumber < 0) {
    throw new Error("Starting number must be a non-negative whole number.");
  }
  if (
    !Number.isSafeInteger(input.zeroPadding) ||
    input.zeroPadding < 0 ||
    input.zeroPadding > 10
  ) {
    throw new Error("Number padding must be a whole number from 0 to 10.");
  }
  if (input.startNumber > Number.MAX_SAFE_INTEGER - (input.count - 1)) {
    throw new Error("The generated court numbers are too large.");
  }
  validateCapacity(input.maxPlayers);
  const prefix = trimCourtName(input.prefix);
  if (!prefix || prefix.trim() === "") {
    throw new Error("Court prefix is required.");
  }
  const tags = normalizeCourtTags(input.tags);
  validateTags(tags);

  return Array.from({ length: input.count }, (_, offset) => {
    const number = String(input.startNumber + offset).padStart(
      input.zeroPadding,
      "0",
    );
    const name = trimCourtName(`${prefix} ${number}`);
    if (Array.from(name).length > 255) {
      throw new Error("Generated court names must be 255 characters or fewer.");
    }
    return {
      name,
      status: "enabled",
      tags,
      ...(input.maxPlayers === undefined
        ? {}
        : { max_players: input.maxPlayers }),
    };
  });
}

interface CsvRow {
  fields: string[];
  rowNumber: number;
}

function parseCsvRows(value: string): {
  rows: CsvRow[];
  issue?: CourtDraftIssue;
} {
  const rows: CsvRow[] = [];
  let fields: string[] = [];
  let field = "";
  let quoted = false;
  let sourceLine = 1;
  let rowStartLine = 1;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === '"') {
      if (quoted && value[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (character === "," && !quoted) {
      fields.push(field);
      field = "";
      continue;
    }
    if ((character === "\n" || character === "\r") && !quoted) {
      fields.push(field);
      if (fields.some((cell) => cell.length > 0)) {
        rows.push({ fields, rowNumber: rowStartLine });
      }
      fields = [];
      field = "";
      if (character === "\r" && value[index + 1] === "\n") index += 1;
      sourceLine += 1;
      rowStartLine = sourceLine;
      continue;
    }
    if (character === "\n" || character === "\r") sourceLine += 1;
    field += character;
  }

  if (quoted) {
    return {
      rows,
      issue: {
        rowNumber: Math.max(1, rowStartLine - 1),
        field: "csv",
        code: "invalid_csv",
        message: "A quoted CSV cell is not closed.",
      },
    };
  }
  fields.push(field);
  if (fields.some((cell) => cell.length > 0)) {
    rows.push({ fields, rowNumber: rowStartLine });
  }
  return { rows };
}

export function validateCourtDraft(courts: CourtCreate[]): CourtDraftIssue[] {
  if (courts.length === 0) {
    return [
      {
        rowNumber: 1,
        field: "csv",
        code: "too_few_rows",
        message: "Add at least one court before review.",
      },
    ];
  }
  const counts = new Map<string, number>();
  for (const court of courts) {
    const key = draftDuplicateKey(court.name);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const issues = courts.flatMap((court, index) => {
    const issues: CourtDraftIssue[] = [];
    const normalizedName = trimCourtName(court.name);
    const key = draftDuplicateKey(court.name);
    if (
      !normalizedName ||
      normalizedName.trim() === "" ||
      Array.from(normalizedName).length > 255
    ) {
      issues.push({
        rowNumber: index + 1,
        field: "name",
        code: "invalid_name",
        message: "Court name is required and must be 255 characters or fewer.",
      });
    }
    if (
      court.max_players != null &&
      (!Number.isInteger(court.max_players) ||
        court.max_players < 1 ||
        court.max_players > 50)
    ) {
      issues.push({
        rowNumber: index + 1,
        field: "max_players",
        code: "invalid_capacity",
        message: "Court capacity must be a whole number from 1 to 50.",
      });
    }
    const tags = court.tags ?? [];
    if (
      tags.length > 20 ||
      tags.some((tag) => !tag.trim() || Array.from(tag.trim()).length > 50)
    ) {
      issues.push({
        rowNumber: index + 1,
        field: "tags",
        code: "invalid_tags",
        message: "Use at most 20 non-blank tags of up to 50 characters.",
      });
    }
    if ((counts.get(key) ?? 0) >= 2) {
      issues.push({
        rowNumber: index + 1,
        field: "name",
        code: "duplicate_in_draft",
        message: "Another row has the same court name.",
      });
    }
    return issues;
  });
  if (courts.length > MAX_BULK_COURTS) {
    issues.unshift({
      rowNumber: MAX_BULK_COURTS + 1,
      field: "csv",
      code: "too_many_rows",
      message: "A bulk request can contain at most 60 courts.",
    });
  }
  return issues;
}

export function parseCourtCsv(value: string): ParsedCourtCsv {
  const parsed = parseCsvRows(value);
  if (parsed.issue) return { courts: [], issues: [parsed.issue] };
  if (parsed.rows.length === 0) {
    return {
      courts: [],
      issues: [
        {
          rowNumber: 1,
          field: "csv",
          code: "invalid_csv",
          message: "Paste a CSV header and at least one court row.",
        },
      ],
    };
  }

  const [header, ...dataRows] = parsed.rows;
  const normalizedHeader = header.fields.map((cell, index) =>
    (index === 0 ? cell.replace(/^\uFEFF/, "") : cell).trim().toLowerCase(),
  );
  if (
    normalizedHeader.length !== 3 ||
    normalizedHeader[0] !== "name" ||
    normalizedHeader[1] !== "capacity" ||
    normalizedHeader[2] !== "tags"
  ) {
    return {
      courts: [],
      issues: [
        {
          rowNumber: 1,
          field: "csv",
          code: "invalid_csv",
          message: "CSV header must be name,capacity,tags.",
        },
      ],
    };
  }
  if (dataRows.length > MAX_BULK_COURTS) {
    return {
      courts: [],
      issues: [
        {
          rowNumber: MAX_BULK_COURTS + 1,
          field: "csv",
          code: "too_many_rows",
          message: "A bulk request can contain at most 60 courts.",
        },
      ],
    };
  }
  if (dataRows.length === 0) {
    return {
      courts: [],
      issues: [
        {
          rowNumber: 1,
          field: "csv",
          code: "invalid_csv",
          message: "CSV must contain at least one court row.",
        },
      ],
    };
  }

  const courts: CourtCreate[] = [];
  const courtSourceRows: number[] = [];
  const issues: CourtDraftIssue[] = [];
  dataRows.forEach((row, index) => {
    const rowNumber = index + 1;
    if (row.fields.length !== 3) {
      issues.push({
        rowNumber,
        field: "csv",
        code: "invalid_csv",
        message: "Each CSV row must have name, capacity, and tags columns.",
      });
      return;
    }
    const name = trimCourtName(row.fields[0]);
    const capacityValue = row.fields[1].trim();
    const tags = normalizeCourtTags(row.fields[2].split("|"));
    const capacity = capacityValue === "" ? undefined : Number(capacityValue);
    let valid = true;
    if (!name || name.trim() === "" || Array.from(name).length > 255) {
      issues.push({
        rowNumber,
        field: "name",
        code: "invalid_name",
        message: "Court name is required and must be 255 characters or fewer.",
      });
      valid = false;
    }
    try {
      validateCapacity(capacity);
    } catch (error) {
      issues.push({
        rowNumber,
        field: "max_players",
        code: "invalid_capacity",
        message: error instanceof Error ? error.message : "Invalid capacity.",
      });
      valid = false;
    }
    try {
      validateTags(tags);
    } catch (error) {
      issues.push({
        rowNumber,
        field: "tags",
        code: "invalid_tags",
        message: error instanceof Error ? error.message : "Invalid tags.",
      });
      valid = false;
    }
    if (!valid) return;
    courts.push({
      name,
      status: "enabled",
      tags,
      ...(capacity === undefined ? {} : { max_players: capacity }),
    });
    courtSourceRows.push(rowNumber);
  });

  const draftIssues = validateCourtDraft(courts).map((issue) => ({
    ...issue,
    rowNumber: courtSourceRows[issue.rowNumber - 1] ?? issue.rowNumber,
  }));

  return {
    courts,
    issues: [...issues, ...draftIssues],
  };
}
